import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SigilMark } from "./Sigil";
import { scrollToSection } from "./Navbar";

const HERO_IMG =
  "https://images.pexels.com/photos/7978043/pexels-photo-7978043.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = ["Ancient Wisdom", "Unlocked for", "Modern Life"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const sigilY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const particles = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        id: i,
        left: `${(i * 23.7) % 100}%`,
        top: `${(i * 37.3) % 100}%`,
        size: 1 + ((i * 7) % 3),
        delay: `${(i * 0.6) % 7}s`,
        duration: `${5 + ((i * 13) % 6)}s`,
      })),
    []
  );

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Candlelit grimoire"
          className="w-full h-full object-cover opacity-50 scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/50 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0A090B_85%)]" />
      </motion.div>

      {particles.map((p) => (
        <span
          key={p.id}
          className="arcane-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
        />
      ))}

      <motion.div
        style={{ y: sigilY, opacity: fade }}
        className="absolute right-[6%] top-[18%] hidden lg:block text-gold/25 -z-0"
      >
        <SigilMark className="w-[420px] h-[420px]" spin />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-8"
          data-testid="hero-overline"
        >
          Est. upon the Emerald Tablet · Hermetic · Solomonic · Folk
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.08] tracking-tight text-parchment max-w-4xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-gold text-glow-gold" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.18, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-parchment-muted leading-relaxed"
          data-testid="hero-subheadline"
        >
          Draw upon the Hermetic Emerald Tablet, the Picatrix, the Key of Solomon and
          sacred folk magic to heal health, ignite wealth, restore love, resolve
          disputes, and command career destiny.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-12 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-explore-services-btn"
            onClick={() => scrollToSection("services")}
            className="bg-gold text-ink text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:bg-amber transition-colors duration-300"
          >
            Explore Arcane Services
          </button>
          <button
            data-testid="hero-book-consultation-btn"
            onClick={() => scrollToSection("pricing")}
            className="border border-gold/60 text-gold text-xs uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:bg-gold/10 transition-colors duration-300"
          >
            Book Ritual Consultation
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/50 text-[10px] uppercase tracking-[0.4em] font-mono"
      >
        Scroll to descend
      </motion.div>
    </section>
  );
};
